import { useEffect, useState } from "react";
import type { Locale } from "@/shared/i18n";
import { readLocaleCookie } from "@/shared/i18n/locale-cookie";
import type { CrashFallbackProps } from "@/shared/ui/ErrorBoundary";
import { TYPE_SCALE_PX } from "@/shared/ui/type-scale";

export function RootErrorFallback({ error, reset }: CrashFallbackProps) {
  const [locale] = useState<Locale>(() => readLocaleCookie() ?? "sv");
  const [where] = useState(() => ({
    path: window.location.pathname + window.location.search,
    at: new Date(),
  }));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    console.error("[simon] root-layout error caught by RootErrorFallback:", error);
  }, [error]);

  const copy = ROOT_ERROR_COPY[locale];

  function copyReport() {
    navigator.clipboard.writeText(crashReport(error, where)).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        flex: "1 1 auto",
        background: BG,
        color: TEXT_STRONG,
        fontFamily: SANS,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        boxSizing: "border-box",
      }}
    >
      <div
        role="alert"
        style={{
          width: "100%",
          maxWidth: 560,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          background: PAPER,
          border: `1px solid ${LINE}`,
          borderRadius: 8,
          padding: "18px 20px",
        }}
      >
        <p style={{ margin: 0, fontSize: TYPE_SCALE_PX.md, color: TEXT_STRONG }}>{copy.heading}</p>
        <div
          style={{
            marginTop: 4,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 16,
          }}
        >
          <button
            type="button"
            onClick={() => reset()}
            style={{
              ...BUTTON,
              border: "1px solid transparent",
              background: TEXT_STRONG,
              color: PAPER,
            }}
          >
            {copy.reload}
          </button>
          <a href="/" style={{ fontSize: TYPE_SCALE_PX.sm, color: BRAND, textDecoration: "none" }}>
            {copy.home}
          </a>
        </div>
        <details style={{ marginTop: 4 }}>
          <summary style={{ cursor: "pointer", fontSize: TYPE_SCALE_PX.xs, color: TEXT_MUTED }}>
            {copy.detailsToggle}
          </summary>
          <div style={{ marginTop: 8, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <pre
              style={{
                userSelect: "all",
                flex: "1 1 auto",
                minWidth: 0,
                margin: 0,
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                borderRadius: 8,
                border: `1px solid ${LINE}`,
                background: "rgba(26, 26, 26, 0.05)",
                padding: "8px 10px",
                fontFamily: MONO,
                fontSize: TYPE_SCALE_PX.xs,
                lineHeight: 1.6,
                color: TEXT_DIM,
              }}
            >
              {`${errorLine(error)}\n${where.path} · ${where.at.toLocaleTimeString("sv-SE")}`}
            </pre>
            <button
              type="button"
              onClick={copyReport}
              style={{
                ...BUTTON,
                border: `1px solid ${LINE}`,
                background: PAPER,
                color: TEXT_STRONG,
              }}
            >
              {copied ? copy.copied : copy.copy}
            </button>
          </div>
        </details>
      </div>
    </div>
  );
}

const ROOT_ERROR_COPY = {
  sv: {
    heading: "Simon kunde inte starta.",
    reload: "Ladda om",
    home: "Till startsidan",
    detailsToggle: "Detaljer",
    copy: "Kopiera",
    copied: "Kopierat",
  },
  en: {
    heading: "Simon could not start.",
    reload: "Reload",
    home: "Back to the start page",
    detailsToggle: "Details",
    copy: "Copy",
    copied: "Copied",
  },
} as const satisfies Record<Locale, Record<string, string>>;

const BG = "#f4f4ee";
const PAPER = "#fbfbf5";
const TEXT_STRONG = "#1a1a1a";
const TEXT_DIM = "#61615f";
const TEXT_MUTED = "#898985";
const LINE = "#e6e6e0";
const BRAND = "#5f54eb";
const SANS =
  'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const BUTTON = {
  height: 32,
  borderRadius: 8,
  padding: "0 12px",
  fontFamily: SANS,
  fontSize: TYPE_SCALE_PX.sm,
  fontWeight: 500,
  whiteSpace: "nowrap",
  cursor: "pointer",
} as const;

function crashReport(error: Error, where: { path: string; at: Date }): string {
  return [errorLine(error), where.path, where.at.toISOString()].join("\n");
}

function errorLine(error: Error): string {
  return `${error.name || "Error"}: ${error.message || "(no message)"}`;
}
